import { signInWithPopup } from 'firebase/auth';
import { Toast } from '../../../components/Common/Toast';
import { auth, provider } from '../../../firebase';
import { AppDispatch } from '../../../store/store';
import { setRedirectURL, setUser } from '../../../store/userSlice';
import { callAPI, reloadToken } from '../../../services/api.service';
import { UserAuthTypeEnum } from '../../../interfaces/user.interface';
import { mapToUserData } from '../../Builder/components/SignUp/SignUp.mapper';
import { autoFetchUserDetails, forceFetchUserDetails } from '../../../store/thunks/user.thunk';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import { resolveWpProjectIdFromProjectParam } from './wp-project.service';
import { getTeacherRedirectURL } from '../../Teacher/utils/onboarding.utils';

export const handleTeacherGoogleSignUp = async (
  setGoogleLoader: (loading: boolean) => void,
  dispatch: AppDispatch,
  navigateTo: (url: string) => void,
  userType: 'teacher' | 'student' = 'teacher',
  tallyProjectId?: string,
  tallyLeadId?: string,
) => {
  try {
    setGoogleLoader(true);
    const result: any = await signInWithPopup(auth, provider);

    const email = result?.user?.email;
    const firebaseId = result?.user?.uid;

    if (!email) {
      Toast('Failed to get email from Google account', true);
      return;
    }

    // Extract name from Google display name
    let name = result?.user?.displayName ? result.user.displayName.split(' ') : ['', ''];
    const firstName = name[0] || '';
    const lastName = name[1] || '';

    // Create teacher user data
    const teacherData = {
      email,
      firstName,
      lastName,
      authType: UserAuthTypeEnum.google,
      firebaseId,
      isAdult: true,
      accessToken: result?.user?.accessToken,
      // Teacher-specific fields
      confirm: true, // Teacher confirmation
      marketing: false, // Default to false for teachers
      password: '', // Required by UserData interface
      promotionalEmail: false, // Required by UserData interface
      updateEmail: false, // Required by UserData interface
      phoneNumber: '', // Optional but included for completeness
    };

    const resolvedProjectId = await resolveWpProjectIdFromProjectParam(tallyProjectId || null);

    const userData = {
      ...mapToUserData({
        signupViaGoogle: teacherData,
        values: teacherData,
        user: null,
      }),
      ...(resolvedProjectId ? { tallyProjectId: resolvedProjectId } : {}),
      ...(tallyLeadId ? { leadId: tallyLeadId } : {}),
    };

    const apiURL = userType === 'teacher' ? 'api/auth/teacher/signUp' : 'api/auth/student/signUp';

    const response = await callAPI(apiURL, userData);

    if (response?.status === 200 || response?.status === 201) {
      // Update Firebase ID in the backend
      await callAPI('api/update-firebase-id', {
        email: email,
        firebaseId: firebaseId,
      });

      // Clear any existing tokens and reload
      TokenCookieManager.removeToken();
      await reloadToken();

      dispatch(
        forceFetchUserDetails((user) => {
          if (!user) {
            return;
          }
          dispatch(setUser(user));
          dispatch(autoFetchUserDetails());
          Toast('Teacher account created successfully!');
          dispatch(setRedirectURL(getTeacherRedirectURL(user) || '/'));
        }),
      );
    } else {
      Toast(response?.message || 'Failed to create teacher account', true);
    }
  } catch (error: any) {
    if (error.code === 'auth/popup-closed-by-user') {
      return;
    }

    console.error('Teacher Google signup error:', error);
    Toast('Something went wrong during teacher signup', true);
  } finally {
    setGoogleLoader(false);
  }
};
